import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { Message } from './Header/components/StandardMessage';

interface GlobalLoadingContextProps {
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
  headerMessage: Message | undefined;
  setHeaderMessage: (message: Message | undefined) => void;
}

export const GlobalLoadingContext = createContext<GlobalLoadingContextProps>({
  globalLoading: false,
  setGlobalLoading: () => {},
  headerMessage: undefined,
  setHeaderMessage: () => {},
});

export const GlobalLoadingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [globalLoading, setGlobalLoading] = useState(false);
  const [headerMessage, setHeaderMessage] = useState<Message | undefined>(
    undefined
  );
  const value = useMemo(
    () => ({
      globalLoading,
      setGlobalLoading,
      headerMessage,
      setHeaderMessage,
    }),
    [globalLoading, headerMessage, setHeaderMessage]
  );

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
    </GlobalLoadingContext.Provider>
  );
};

export const useGlobalLoading = () => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error(
      'useGlobalLoading must be used within a GlobalLoadingProvider'
    );
  }
  return context;
};
